import type { Question, Stressor, Tag } from "@/lib/cvcs-types";

const tag = (code: string, weight = 1): Tag => ({ code: code.replace(/^[-+]/, "") as Tag["code"], weight });
const tags = (spec: string): Tag[] =>
  spec
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(part => tag(part, part.startsWith("-") ? -1 : 1));

const likert = (id: string, module: string, text: string, spec: string): Question => ({
  id,
  module,
  type: "likert",
  text,
  tags: tags(spec),
  allowNotSure: true,
});

const multi = (id: string, text: string, options: string[], maxSelections?: number): Question => ({
  id,
  module: "Warm-up",
  type: "multiselect",
  text,
  options,
  scored: false,
  ...(maxSelections ? { maxSelections } : {}),
});

const trade = (
  id: string,
  module: string,
  text: string,
  optionA: string,
  specA: string,
  optionB: string,
  specB: string,
  stressor?: Stressor,
): Question => ({
  id,
  module,
  type: "tradeoff",
  text,
  optionA,
  optionB,
  tagsA: tags(specA),
  tagsB: tags(specB),
  allowNotSure: true,
  ...(stressor ? { stressor } : {}),
} as Question);

const importance = (module: string): Question => ({
  id: `imp-${module.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
  module: "Importance",
  type: "importance",
  text: `How important were your answers in ${module}?`,
  targetModule: module,
});

export const modules = [
  "Economic order and redistribution",
  "State capacity, localism, and bureaucracy",
  "Liberty, authority, and order",
  "Culture, religion, family, and identity",
  "Democracy, institutions, expertise, and populism",
  "Nation, migration, and global order",
  "Environment, technology, and the future",
  "Crime, punishment, and civil peace",
  "Work, merit, status, and daily life",
  "Media, knowledge, and disagreement",
  "Nonpolitical value analogs",
  "Stress-test vignettes",
] as const;

const labels = [
  "progressive",
  "liberal",
  "moderate",
  "conservative",
  "libertarian",
  "socialist",
  "social democrat",
  "nationalist",
  "populist",
  "green",
  "anarchist",
  "religious conservative",
  "apolitical",
  "none",
  "other",
];

export const questionBank: Question[] = [
  multi("w1", "Which political label, if any, feels closest to you?", labels, 1),
  multi("w2", "Which label feels least accurate for you?", labels, 1),
  { id: "w3", module: "Warm-up", type: "likert", text: "I follow politics closely and find it genuinely interesting.", tags: [], allowNotSure: false },
  { id: "w4", module: "Warm-up", type: "likert", text: "My political views have stayed mostly stable over the past few years.", tags: [], allowNotSure: false },
  { id: "w5", module: "Warm-up", type: "likert", text: "I am willing to change my mind when I encounter strong evidence against my view.", tags: [], allowNotSure: false },
  multi("w6", "Pick your top three political concerns.", ["economy", "healthcare", "crime", "immigration", "climate", "corruption", "civil liberties", "religion/culture", "education", "foreign policy", "housing", "technology", "inequality", "democracy", "other"], 3),
  multi("w7", "Pick the trade-off you find hardest.", ["liberty/order", "equality/merit", "local/national", "nation/world", "expertise/democracy", "growth/ecology", "pluralism/tradition"], 1),

  likert("b1", modules[0], "A decent society should guarantee basic housing, healthcare, and food even if taxes rise.", "+EE +SC -MA"),
  likert("b2", modules[0], "A dynamic economy needs low taxes and fewer rules even if outcomes become more unequal.", "+MA +HI -EE -SC"),
  likert("b3", modules[0], "Essential services should be public or tightly regulated when private firms fail to serve everyone.", "+SC +EE -MA"),
  likert("b4", modules[0], "It is usually better to give people direct cash support than have government run more services.", "+CL +MA -SC"),
  likert("b5", modules[0], "Labor unions are important counterweights to concentrated business power.", "+EE +RA -HI"),
  likert("b6", modules[0], "Large corporations should face stricter political limits than small businesses or individuals.", "+EE +SC +DD"),
  likert("b7", modules[0], "People who take major financial risks deserve most of the profits if they succeed.", "+MA +HI +PROPORTION"),
  likert("b8", modules[0], "Inheritance taxes are justified when wealth becomes permanent class advantage.", "+EE +RA -HI"),
  importance(modules[0]),

  likert("c9", modules[1], "National problems often require national solutions, even when local preferences differ.", "+SC -LO"),
  likert("c10", modules[1], "Local communities should control most services even if quality varies by region.", "+LO -SC"),
  likert("c11", modules[1], "A competent bureaucracy can protect freedom better than a weak government can.", "+SC +IC +ET"),
  likert("c12", modules[1], "Regulations should be judged by measurable outcomes, not by whether they sound pro-government or anti-government.", "+ET +IC"),
  likert("c13", modules[1], "Government agencies should be allowed to move quickly during crises, then face review afterward.", "+SC +IC +SECURITY"),
  likert("c14", modules[1], "It is safer to keep state power limited even if that leaves some social problems unsolved.", "+CL +MA -SC"),
  likert("c15", modules[1], "Public institutions should be run more like businesses, with targets, audits, and manager discretion.", "+ET +MA"),
  likert("c16", modules[1], "When both markets and government fail, cooperatives and mutual aid should fill the gap.", "+LO +EE +CL -HI"),
  importance(modules[1]),

  likert("d17", modules[2], "The state should almost never restrict speech because offensive ideas are best answered, not banned.", "+CL -OA"),
  likert("d18", modules[2], "Some speech should be legally restricted when it predictably intimidates vulnerable groups.", "+SC +SP -CL"),
  likert("d19", modules[2], "Stable communities need respect for authority, discipline, and shared norms.", "+OA +SECURITY"),
  likert("d20", modules[2], "People should be free to live in unconventional ways even if most neighbors disapprove.", "+CL +SP -OA"),
  likert("d21", modules[2], "Public disorder harms ordinary people enough that enforcement should be stricter.", "+OA +SECURITY"),
  likert("d22", modules[2], "Police powers should be limited even if that makes some crimes harder to investigate.", "+CL +IC -OA"),
  likert("d23", modules[2], "Schools should teach students to question authority and inherited tradition.", "+CO +CL -OA"),
  likert("d24", modules[2], "Schools should teach duty, patriotism, and respect for inherited institutions.", "+OA +NS +LOYALTY"),
  importance(modules[2]),

  likert("e25", modules[3], "Religious or traditional values should influence public law when a community broadly shares them.", "+OA +NS +PURITY -SP"),
  likert("e26", modules[3], "Law should stay neutral between religious and nonreligious ways of life.", "+SP +CL +IC"),
  likert("e27", modules[3], "Gender roles should be mostly left to personal choice, not social expectation.", "+CL +SP +CO"),
  likert("e28", modules[3], "Rapid cultural change can damage social trust even when reforms have good intentions.", "+SECURITY +OA -CO"),
  likert("e29", modules[3], "Historical injustice requires active public repair, not just formal equal treatment today.", "+EE +RA +SP"),
  likert("e30", modules[3], "Equal treatment under law matters more than group-conscious remedies.", "+IC +CL +PROPORTION -RA"),
  likert("e31", modules[3], "Communities should be allowed to preserve distinctive customs even if outsiders dislike them.", "+LO +NS"),
  likert("e32", modules[3], "Universal individual rights should override local customs when the two conflict.", "+CL +IC +UNIVERSAL -LO"),
  importance(modules[3]),

  likert("f33", modules[4], "Courts, election administrators, and independent agencies should constrain elected majorities.", "+IC -DD"),
  likert("f34", modules[4], "Majorities should be able to enact their agenda without unelected institutions blocking them.", "+DD -IC"),
  likert("f35", modules[4], "Political leaders should mostly follow expert advice on technical questions.", "+ET -DD"),
  likert("f36", modules[4], "Experts should advise, but elected representatives should decide even when experts strongly disagree.", "+DD -ET"),
  likert("f37", modules[4], "Ordinary people usually have better political judgment than professional politicians.", "+DD -ET"),
  likert("f38", modules[4], "Compromise with political opponents is usually necessary for legitimate government.", "+IC -DD"),
  likert("f39", modules[4], "When a system is corrupt, an outsider leader should get unusually broad powers to clean it up.", "+DD +OA -IC"),
  likert("f40", modules[4], "Transparency and audits matter more than trusting any leader’s intentions.", "+IC +ET -DD"),
  importance(modules[4]),

  likert("g41", modules[5], "A country has a special obligation to its own citizens before outsiders.", "+NS -GC"),
  likert("g42", modules[5], "Human rights should not depend much on citizenship or birthplace.", "+GC +SP +UNIVERSAL -NS"),
  likert("g43", modules[5], "Immigration levels should be reduced when assimilation or public services are under strain.", "+NS +SECURITY -GC"),
  likert("g44", modules[5], "Immigration generally strengthens a country when institutions adapt well.", "+GC +SP +CO"),
  likert("g45", modules[5], "Trade policy should protect domestic workers even if consumer prices rise.", "+NS +EE -MA"),
  likert("g46", modules[5], "International agreements are worth some loss of national policy freedom.", "+GC +IC -NS"),
  likert("g47", modules[5], "Military force should be used only when a direct vital interest is at stake.", "+CL -GC"),
  likert("g48", modules[5], "Democracies have some responsibility to support democratic movements abroad.", "+GC +IC"),
  importance(modules[5]),

  likert("h49", modules[6], "Climate and ecological risk justify major regulation even when costs are high now.", "+EP +SC -MA"),
  likert("h50", modules[6], "Innovation and markets will solve environmental problems better than restrictive rules.", "+MA +CO -EP"),
  likert("h51", modules[6], "The interests of future generations should count heavily in today’s laws.", "+EP +UNIVERSAL"),
  likert("h52", modules[6], "Energy policy should prioritize reliability and affordability over emissions cuts.", "+SECURITY +MA -EP"),
  likert("h53", modules[6], "Dangerous technologies should be paused until society understands their consequences.", "+EP +SECURITY -CO"),
  likert("h54", modules[6], "Society should tolerate more technological risk to get medical, economic, or scientific gains.", "+CO +MA -EP"),
  likert("h55", modules[6], "Local communities should have veto power over major infrastructure projects.", "+LO +CL -SC"),
  likert("h56", modules[6], "National infrastructure should be built even when local objections are strong.", "+SC -LO"),
  importance(modules[6]),

  likert("i57", modules[7], "Punishment should focus more on rehabilitation than retribution.", "+RA +CARE -OA"),
  likert("i58", modules[7], "Some crimes deserve harsh punishment because moral accountability matters.", "+OA +PROPORTION"),
  likert("i59", modules[7], "Poverty and social conditions explain much crime, so prevention must be structural.", "+RA +EE"),
  likert("i60", modules[7], "Individuals remain primarily responsible for crime regardless of social conditions.", "-RA +OA +PROPORTION"),
  likert("i61", modules[7], "Surveillance tools are acceptable if tightly audited and shown to reduce serious crime.", "+SC +SECURITY +ET -CL"),
  likert("i62", modules[7], "It is better to accept some security risk than normalize surveillance.", "+CL -SECURITY"),
  likert("i63", modules[7], "Political violence is never acceptable in a functioning democracy.", "+IC +CL"),
  likert("i64", modules[7], "Civil disobedience can be legitimate when institutions ignore serious injustice.", "+DD +CL +RA"),
  importance(modules[7]),

  likert("j65", modules[8], "Hard work is usually the main driver of success.", "-RA +HI +PROPORTION"),
  likert("j66", modules[8], "Luck, inherited advantage, and institutions shape success more than most people admit.", "+RA +EE -HI"),
  likert("j67", modules[8], "Competitive ranking is healthy because it motivates excellence.", "+HI +MA +ACHIEVE"),
  likert("j68", modules[8], "People do better when cooperation is valued over competition.", "+EE +CARE -HI"),
  likert("j69", modules[8], "A good workplace gives employees real voice over major decisions.", "+EE +LO"),
  likert("j70", modules[8], "Owners and executives should retain final authority because they carry the risk.", "+MA +HI"),
  likert("j71", modules[8], "It is acceptable for society to honor excellence even if that creates status differences.", "+HI +ACHIEVE"),
  likert("j72", modules[8], "Status differences are socially corrosive even when earned.", "+EE -HI"),
  importance(modules[8]),

  likert("k73", modules[9], "I would rather have messy open debate than official truth management.", "+CL -ET -SC"),
  likert("k74", modules[9], "Platforms should demote content likely to cause real-world harm, even when that content is legal.", "+SC +ET -CL"),
  likert("k75", modules[9], "When institutions repeatedly fail, suspicion toward official narratives is reasonable.", "+DD -ET"),
  likert("k76", modules[9], "Strong evidence should change my view even when it hurts my political side.", "+ET +IC"),
  likert("k77", modules[9], "A claim should be treated cautiously when all major institutions agree too quickly.", "+DD -ET"),
  likert("k78", modules[9], "Conspiracy thinking is dangerous because it makes democratic accountability impossible.", "+ET +IC -DD"),
  likert("k79", modules[9], "Journalists should be adversarial toward all powerful institutions rather than patriotic or movement-oriented.", "+IC +DD -NS"),
  likert("k80", modules[9], "News organizations have civic duties beyond giving audiences what they want.", "+IC +ET"),
  importance(modules[9]),

  likert("l81", modules[10], "In a group project, I prefer clear roles and a leader to open-ended collaboration.", "+OA -DD"),
  likert("l82", modules[10], "I would rather try a promising new method than preserve a familiar one.", "+CO"),
  likert("l83", modules[10], "When a rule produces unfair outcomes, bending it can be justified.", "+CARE -IC"),
  likert("l84", modules[10], "Rules matter most when they protect everyone from favoritism.", "+IC"),
  likert("l85", modules[10], "Loyalty to family or close community should outrank abstract principles.", "+LOYALTY +NS -UNIVERSAL"),
  likert("l86", modules[10], "I feel obligations toward strangers far away if my choices affect them.", "+GC +UNIVERSAL +CARE"),
  likert("l87", modules[10], "I prefer safety margins and backup plans over maximum speed or gain.", "+EP +SECURITY -CO"),
  likert("l88", modules[10], "I dislike waste and disorder even when no one is directly harmed.", "+PURITY +OA"),
  importance(modules[10]),

  trade("s1", modules[11], "Public health emergency", "Government may temporarily restrict gatherings if independent review occurs afterward.", "+SC +SECURITY +IC", "Civil liberties should remain mostly untouched even during emergencies.", "+CL -SC", "threat"),
  trade("s2", modules[11], "Economic crash", "The state should borrow heavily to preserve jobs and incomes.", "+SC +EE", "The state should avoid heavy intervention and let recovery come through private adjustment.", "+MA -SC", "scarcity"),
  trade("s3", modules[11], "Crime spike", "Expand police presence and investigative powers with oversight.", "+OA +SECURITY +SC", "Focus on social prevention and limit coercive powers.", "+RA +CL +EE", "threat"),
  trade("s4", modules[11], "Corrupt political class", "Use direct referenda, outsider candidates, and term limits to break elite control.", "+DD -IC", "Strengthen audits, courts, disclosure rules, and professional administration.", "+IC +ET -DD", "corruption"),
  trade("s5", modules[11], "Climate disaster", "Use mandates, bans, and public investment to force rapid transition.", "+EP +SC -MA", "Use incentives, innovation, and consumer choice to transition without heavy coercion.", "+MA +CO -SC", "scarcity"),
  trade("s6", modules[11], "Misinformation wave", "Platforms and regulators should reduce dangerous falsehoods during high-risk events.", "+ET +SC -CL", "Authorities should not manage truth because that power will be abused.", "+CL +DD -ET", "complexity"),
  trade("s7", modules[11], "Large migration surge", "Admit more people and scale institutions around humanitarian obligation.", "+GC +CARE +SP", "Slow admissions until housing, schools, and social trust can absorb the change.", "+NS +SECURITY", "culture"),
  trade("s8", modules[11], "War involving an ally", "Honor alliances even when costs are high.", "+GC +LOYALTY", "Avoid foreign entanglement unless directly attacked.", "+NS +CL", "security"),
  trade("s9", modules[11], "Offensive local tradition", "Let the community preserve it unless it directly violates individual rights.", "+LO +NS", "Override it when it conflicts with equal civic status.", "+SP +IC +UNIVERSAL", "culture"),
  trade("s10", modules[11], "AI automation shock", "Tax automation gains and provide universal economic security.", "+EE +SC +RA", "Prioritize retraining, entrepreneurship, and flexible markets.", "+MA +CO", "complexity"),
  trade("s11", modules[11], "Extremist but legal group", "Allow the group to assemble and speak unless it directly incites violence.", "+CL +IC", "Restrict it before it intimidates targets or normalizes political violence.", "+SECURITY +SP -CL", "threat"),
  trade("s12", modules[11], "Local infrastructure veto", "Local residents should be able to block major projects affecting them.", "+LO +CL", "National needs should override local vetoes when benefits are broad.", "+SC +GC -LO", "complexity"),
  importance(modules[11]),
];
